import {
  computed,
  DestroyRef,
  inject,
  Injectable,
  signal,
} from "@angular/core";
import { ApiService } from "./api.service";
import { notificationsEndpointConfig } from "../configs/endpoints/notifications.endpoint.config";
import { HttpParams } from "@angular/common/http";
import {
  NotificationDto,
  NotificationViewModel,
  PageDto,
  UnreadNotificationsModel,
} from "../../app/components/notifications/notifications.model";
import { NotificationsPageType } from "../../app/components/notifications/notifications.types";
import { NotificationsPageTypeEnum } from "../../app/components/notifications/notifications.enum";
import {
  NOTIFICATIONS_PANEL_PAGE_SIZE,
  REFRESH_UNREAD_COUNT_INTERVAL,
} from "../../app/components/notifications/notifications.constants";
import { finalize } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class NotificationsManagementService {
  readonly isOpenNotificationsPanel = signal(false);

  private destroyRef = inject(DestroyRef);
  private apiService = inject(ApiService);

  private pageIndex = signal<number>(0);
  private unreadCount = signal<number>(0);
  private panelData = signal<NotificationViewModel[]>([]);
  private pageData = signal<NotificationViewModel[]>([]);
  private totalElements = signal<number>(0);
  private isLoading = signal<boolean>(false);

  private hasMorePanelNotifications: boolean = true;
  private isLoadingPanelNotifications: boolean = false;

  public categories = computed(() => {
    return [
      ...new Set(
        [...this.panelData(), ...this.pageData()].map((item) => item.category),
      ),
    ];
  });

  public readonly data = computed(() => ({
    unreadCount: this.unreadCount(),
    items: {
      page: this.pageData(),
      panel: this.panelData(),
    },
    categories: this.categories(),
    totalElements: this.totalElements(),
    isLoading: this.isLoading(),
  }));

  constructor() {
    this.getUnreadCount();
  }

  public resetAndOpen() {
    this.panelData.set([]);
    this.loadData();
    this.refreshUnreadCount();
    this.isOpenNotificationsPanel.set(true);
    this.pageIndex.set(0);
  }

  public resetAndClose() {
    this.markNotificationsAsRead();
    this.isOpenNotificationsPanel.set(false);
  }

  public loadData(
    pageIndex = this.pageIndex(),
    pageSize = NOTIFICATIONS_PANEL_PAGE_SIZE,
    type = NotificationsPageTypeEnum.PANEL,
  ) {
    if (!this.hasMorePanelNotifications || this.isLoadingPanelNotifications)
      return;

    this.isLoading.set(true);

    this.isLoadingPanelNotifications = true;

    if (this.hasMorePanelNotifications) {
      const params: HttpParams = new HttpParams()
        .set("page_index", pageIndex)
        .set("page_size", pageSize);

      this.apiService
        .get<PageDto<NotificationDto>>(
          notificationsEndpointConfig.platform(),
          params,
        )
        .pipe(
          finalize(() => {
            this.isLoadingPanelNotifications = false;
            this.isLoading.set(false);
          }),
        )
        .subscribe({
          next: ({ content, size, numberOfElements, totalElements }): void => {
            if (numberOfElements < size) {
              this.hasMorePanelNotifications = false;
            }
            this.totalElements.set(totalElements);
            this.updateData(type, content);
          },
        });
    }
  }

  public refreshUnreadCount() {
    this.apiService
      .get<UnreadNotificationsModel>(notificationsEndpointConfig.unread())
      .subscribe({
        next: ({ unread_count }): void => {
          this.unreadCount.set(unread_count);
        },
      });
  }

  private mapDtoToViewData = ({
    id,
    category_code,
    payload,
    created_at,
    read_at,
    is_clickable,
  }: NotificationDto) => ({
    id,
    category: category_code,
    description: payload,
    created: new Date(created_at),
    unread: !read_at,
    route: is_clickable,
  });

  private mapItemsDto(items: any): NotificationViewModel[] {
    return [...(items ?? []).map(this.mapDtoToViewData)];
  }

  private updateData(type: NotificationsPageType, items: NotificationDto[]) {
    switch (type) {
      case NotificationsPageTypeEnum.PANEL:
        this.panelData.update((curr) => [...curr, ...this.mapItemsDto(items)]);
        this.pageIndex.set(this.pageIndex() + 1);
        break;
      case NotificationsPageTypeEnum.PAGE:
        this.pageData.set(this.mapItemsDto(items));
        this.markNotificationsAsRead(NotificationsPageTypeEnum.PAGE);
        break;
    }
  }

  private getUnreadCount() {
    this.refreshUnreadCount();
    const interval = setInterval(
      () => this.refreshUnreadCount(),
      REFRESH_UNREAD_COUNT_INTERVAL,
    );
    this.destroyRef.onDestroy(() => clearInterval(interval));
  }

  private getUnreadNotificationsIds(
    notifications: NotificationViewModel[],
  ): number[] {
    return notifications
      .filter((item: NotificationViewModel) => item.unread)
      .map((item) => Number(item.id));
  }

  private markNotificationsAsRead(
    type: NotificationsPageType = NotificationsPageTypeEnum.PANEL,
  ) {
    const unreadNotificationsIds = this.getUnreadNotificationsIds(
      type === NotificationsPageTypeEnum.PANEL
        ? this.panelData()
        : this.pageData(),
    );

    this.hasMorePanelNotifications = true;
    this.pageIndex.set(0);

    if (unreadNotificationsIds.length) {
      this.apiService
        .post(
          { ids: unreadNotificationsIds },
          notificationsEndpointConfig.markRead(),
        )
        .subscribe({
          next: (): void => {},
        });
    }
  }
}
